import React, { useRef } from 'react'
import { X } from 'lucide-react'
import { useForm } from 'react-hook-form'
import type { Step, StepStatus } from '../../types/session.types'
import { useUpdateStepStatus } from '../../hooks/useStepMutations'
import { useCanUpdateStep } from '../../stores/selectors'
import styles from './StepDetailModal.module.css'

export interface StepDetailModalProps {
  step: Step
  sessionId: string
  onClose: () => void
}

interface UpdateForm {
  note: string
}

export function StepDetailModal({ step, sessionId, onClose }: StepDetailModalProps) {
  const { register, handleSubmit } = useForm<UpdateForm>({
    defaultValues: { note: step.note ?? '' },
  })
  const canUpdate = useCanUpdateStep(step.team_id)
  const { mutate: updateStatus, isPending } = useUpdateStepStatus(sessionId)
  const overlayRef = useRef<HTMLDivElement>(null)

  const onSubmit = (newStatus: StepStatus) => (data: UpdateForm) => {
    updateStatus(
      { stepId: step.id, status: newStatus, note: data.note },
      { onSuccess: () => onClose() }
    )
  }

  return (
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={(e) => e.target === overlayRef.current && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="step-modal-title"
    >
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 id="step-modal-title" className={styles.modalTitle}>
            Step {step.order_index + 1}: {step.name}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Description */}
        {step.description && (
          <div className={styles.description}>
            <pre className={styles.descriptionText}>{step.description}</pre>
          </div>
        )}

        {/* Metadata */}
        <div className={styles.meta}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Status</span>
            <span className={styles.metaValue}>
              {step.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Est. Duration</span>
            <span className={styles.metaValue}>{step.estimated_duration_mins} min</span>
          </div>
          {step.actual_duration_mins !== undefined && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Actual Duration</span>
              <span className={styles.metaValue}>
                {Math.round(step.actual_duration_mins)} min
              </span>
            </div>
          )}
          {step.retry_count > 0 && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Retries</span>
              <span className={styles.metaValue}>{step.retry_count}</span>
            </div>
          )}
        </div>

        {/* Dependencies */}
        {step.dependencies && step.dependencies.length > 0 && (
          <div className={styles.deps}>
            <span className={styles.depsLabel}>Depends on:</span>
            <span className={styles.depsCount}>{step.dependencies.length} step(s)</span>
          </div>
        )}

        {/* Update form (only for authorized users, non-terminal steps) */}
        {canUpdate && step.status !== 'done' && step.status !== 'skipped' && (
          <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
            <label className={styles.formLabel} htmlFor="step-note">
              Update Note
            </label>
            <textarea
              id="step-note"
              className={styles.textarea}
              {...register('note')}
              placeholder="Add a note (optional)…"
              rows={3}
            />
            <div className={styles.formActions}>
              {step.status === 'ready' && (
                <button
                  type="button"
                  id={`modal-start-${step.id}`}
                  className={styles.btnStart}
                  onClick={handleSubmit(onSubmit('in_progress'))}
                  disabled={isPending}
                >
                  ▶ Start Step
                </button>
              )}
              {step.status === 'in_progress' && (
                <>
                  <button
                    type="button"
                    id={`modal-done-${step.id}`}
                    className={styles.btnDone}
                    onClick={handleSubmit(onSubmit('done'))}
                    disabled={isPending}
                  >
                    ✓ Mark Done
                  </button>
                  <button
                    type="button"
                    id={`modal-fail-${step.id}`}
                    className={styles.btnFail}
                    onClick={handleSubmit(onSubmit('failed'))}
                    disabled={isPending}
                  >
                    ✗ Mark Failed
                  </button>
                  <button
                    type="button"
                    id={`modal-pause-${step.id}`}
                    className={styles.btnPause}
                    onClick={handleSubmit(onSubmit('paused'))}
                    disabled={isPending}
                  >
                    ⏸ Pause
                  </button>
                </>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
